import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  FileText, 
  Plus, 
  X, 
  Upload, 
  Download,
  Eye,
  Trash2,
  Camera,
  Mic,
  Save,
  FileDown,
  AlertTriangle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { securityReportsAPI, sitesAPI } from '../services/api';
import { getErrorMessage } from '../utils/errorHandler';
import SignatureCanvas from 'react-signature-canvas';

const SecurityReports = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const signatureRef = useRef(null);
  
  const [sites, setSites] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [currentReport, setCurrentReport] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    siteId: '',
    periodStart: '',
    periodEnd: '',
    geolocation: '',
    totalIncidents: 0,
    incidents: [],
    interventions: [],
    repairs: '',
    improvements: '',
    observations: '',
    comments: '',
    responsibleName: user?.name || '',
  });

  // Incident form
  const [incidentForm, setIncidentForm] = useState({
    type: 'intrusion',
    description: '',
    measuresTaken: ''
  });

  // Intervention form
  const [interventionForm, setInterventionForm] = useState({
    description: '',
    time: '',
    agentName: ''
  });

  useEffect(() => {
    loadSites();
    loadReports();
    // Get geolocation
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        setFormData(prev => ({
          ...prev,
          geolocation: `${position.coords.latitude}, ${position.coords.longitude}`
        }));
      });
    }
  }, []);

  const loadSites = async () => {
    try {
      const response = await sitesAPI.getAll();
      setSites(response.data || []);
    } catch (error) {
      console.error('Error loading sites:', error);
    }
  };

  const loadReports = async () => {
    try {
      const response = await securityReportsAPI.getAll();
      setReports(response.data || []);
    } catch (error) {
      console.error('Error loading reports:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addIncident = () => {
    if (!incidentForm.description) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir la description de l'incident",
        variant: "destructive",
      });
      return;
    }

    setFormData(prev => ({
      ...prev,
      incidents: [...prev.incidents, { ...incidentForm }],
      totalIncidents: prev.totalIncidents + 1
    }));

    setIncidentForm({
      type: 'intrusion',
      description: '',
      measuresTaken: ''
    });

    toast({
      title: "Incident ajouté",
      description: "L'incident a été ajouté au rapport",
    });
  };

  const removeIncident = (index) => {
    setFormData(prev => ({
      ...prev,
      incidents: prev.incidents.filter((_, i) => i !== index),
      totalIncidents: prev.totalIncidents - 1
    }));
  };

  const addIntervention = () => {
    if (!interventionForm.description) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir la description de l'intervention",
        variant: "destructive",
      });
      return;
    }

    setFormData(prev => ({
      ...prev,
      interventions: [...prev.interventions, { ...interventionForm }]
    }));

    setInterventionForm({
      description: '',
      time: '',
      agentName: ''
    });

    toast({
      title: "Intervention ajoutée",
      description: "L'intervention a été ajoutée au rapport",
    });
  };

  const removeIntervention = (index) => {
    setFormData(prev => ({
      ...prev,
      interventions: prev.interventions.filter((_, i) => i !== index)
    }));
  };

  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.siteId) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner un site",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      // Get signature as base64
      let signature = null;
      if (signatureRef.current && !signatureRef.current.isEmpty()) {
        signature = signatureRef.current.toDataURL();
      }

      const reportData = {
        ...formData,
        responsibleSignature: signature
      };

      const response = await securityReportsAPI.create(reportData);
      
      toast({
        title: "Rapport créé",
        description: "Le rapport de sécurité a été créé avec succès",
      });

      setCurrentReport(response.data);
      loadReports();
      
      // Reset form
      setFormData({
        siteId: '',
        periodStart: '',
        periodEnd: '',
        geolocation: formData.geolocation,
        totalIncidents: 0,
        incidents: [],
        interventions: [],
        repairs: '',
        improvements: '',
        observations: '',
        comments: '',
        responsibleName: user?.name || '',
      });
      clearSignature();
      setShowForm(false);
      
    } catch (error) {
      toast({
        title: "Erreur",
        description: getErrorMessage(error, "Impossible de créer le rapport"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (reportId, format) => {
    try {
      let response;
      if (format === 'pdf') {
        response = await securityReportsAPI.exportPDF(reportId);
      } else if (format === 'word') {
        response = await securityReportsAPI.exportWord(reportId);
      } else if (format === 'excel') {
        response = await securityReportsAPI.exportExcel(reportId);
      }

      toast({
        title: "Export réussi",
        description: response.data.message,
      });
    } catch (error) {
      toast({
        title: "Erreur d'export",
        description: getErrorMessage(error, "Impossible d'exporter le rapport"),
        variant: "destructive",
      });
    }
  };

  const incidentTypes = {
    intrusion: 'Intrusion',
    vol: 'Vol',
    vandalisme: 'Vandalisme',
    alarme: 'Alarme déclenchée',
    incendie: 'Incendie',
    autre: 'Autre'
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Rapports de Sécurité</h1>
          <p className="text-gray-500 mt-1">Créez et gérez vos rapports détaillés</p>
        </div>
        <Button 
          onClick={() => setShowForm(!showForm)} 
          className="flex items-center space-x-2"
        >
          {showForm ? (
            <>
              <X className="w-4 h-4" />
              <span>Annuler</span>
            </>
          ) : (
            <>
              <Plus className="w-4 h-4" />
              <span>Nouveau rapport</span>
            </>
          )}
        </Button>
      </div>

      {/* Form */}
      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>Créer un rapport de sécurité</CardTitle>
            <CardDescription>Remplissez tous les détails du rapport</CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-6">
              {/* Informations générales */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Informations générales</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="siteId">Site *</Label>
                    <select
                      id="siteId"
                      name="siteId"
                      value={formData.siteId}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Sélectionner un site</option>
                      {sites.map(site => (
                        <option key={site.id} value={site.id}>{site.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="geolocation">Géolocalisation</Label>
                    <Input
                      id="geolocation"
                      name="geolocation"
                      value={formData.geolocation}
                      onChange={handleInputChange}
                      placeholder="Auto-détecté"
                      readOnly
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="periodStart">Date de début *</Label>
                    <Input
                      type="date"
                      id="periodStart"
                      name="periodStart"
                      value={formData.periodStart}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="periodEnd">Date de fin *</Label>
                    <Input
                      type="date"
                      id="periodEnd"
                      name="periodEnd"
                      value={formData.periodEnd}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Incidents */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2 text-red-600" />
                  Incidents de sécurité
                </h3>
                
                {/* Liste des incidents */}
                {formData.incidents.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {formData.incidents.map((incident, index) => (
                      <div key={index} className="bg-red-50 border border-red-200 rounded p-3 flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-semibold text-red-900">{incidentTypes[incident.type]}</p>
                          <p className="text-sm text-red-700">{incident.description}</p>
                          {incident.measuresTaken && (
                            <p className="text-xs text-red-600 mt-1">Mesures: {incident.measuresTaken}</p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeIncident(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Formulaire ajout incident */}
                <div className="bg-gray-50 rounded p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Type d'incident</Label>
                      <select
                        value={incidentForm.type}
                        onChange={(e) => setIncidentForm({...incidentForm, type: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        {Object.entries(incidentTypes).map(([key, label]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={incidentForm.description}
                      onChange={(e) => setIncidentForm({...incidentForm, description: e.target.value})}
                      placeholder="Décrivez l'incident..."
                      rows={2}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Mesures prises</Label>
                    <Textarea
                      value={incidentForm.measuresTaken}
                      onChange={(e) => setIncidentForm({...incidentForm, measuresTaken: e.target.value})}
                      placeholder="Quelles mesures ont été prises ?"
                      rows={2}
                    />
                  </div>

                  <Button type="button" onClick={addIncident} size="sm" variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter cet incident
                  </Button>
                </div>
              </div>

              {/* Interventions */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4">Interventions de sécurité</h3>
                
                {/* Liste des interventions */}
                {formData.interventions.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {formData.interventions.map((intervention, index) => (
                      <div key={index} className="bg-blue-50 border border-blue-200 rounded p-3 flex justify-between items-start">
                        <div className="flex-1">
                          <p className="text-sm text-blue-900">{intervention.description}</p>
                          {intervention.agentName && (
                            <p className="text-xs text-blue-700 mt-1">Agent: {intervention.agentName}</p>
                          )}
                          {intervention.time && (
                            <p className="text-xs text-blue-600">Heure: {intervention.time}</p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeIntervention(index)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Formulaire ajout intervention */}
                <div className="bg-gray-50 rounded p-4 space-y-3">
                  <div className="space-y-2">
                    <Label>Description de l'intervention</Label>
                    <Textarea
                      value={interventionForm.description}
                      onChange={(e) => setInterventionForm({...interventionForm, description: e.target.value})}
                      placeholder="Décrivez l'intervention..."
                      rows={2}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nom de l'agent</Label>
                      <Input
                        value={interventionForm.agentName}
                        onChange={(e) => setInterventionForm({...interventionForm, agentName: e.target.value})}
                        placeholder="Nom de l'agent"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Heure</Label>
                      <Input
                        type="time"
                        value={interventionForm.time}
                        onChange={(e) => setInterventionForm({...interventionForm, time: e.target.value})}
                      />
                    </div>
                  </div>

                  <Button type="button" onClick={addIntervention} size="sm" variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter cette intervention
                  </Button>
                </div>
              </div>

              {/* Réparations et améliorations */}
              <div className="border-t pt-6 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="repairs">Réparations effectuées</Label>
                  <Textarea
                    id="repairs"
                    name="repairs"
                    value={formData.repairs}
                    onChange={handleInputChange}
                    placeholder="Décrivez les réparations effectuées..."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="improvements">Améliorations envisagées</Label>
                  <Textarea
                    id="improvements"
                    name="improvements"
                    value={formData.improvements}
                    onChange={handleInputChange}
                    placeholder="Quelles améliorations sont envisagées ?"
                    rows={3}
                  />
                </div>
              </div>

              {/* Observations */}
              <div className="border-t pt-6 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="observations">Observations</Label>
                  <Textarea
                    id="observations"
                    name="observations"
                    value={formData.observations}
                    onChange={handleInputChange}
                    placeholder="Observations générales..."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="comments">Commentaires additionnels</Label>
                  <Textarea
                    id="comments"
                    name="comments"
                    value={formData.comments}
                    onChange={handleInputChange}
                    placeholder="Commentaires..."
                    rows={3}
                  />
                </div>
              </div>

              {/* Signature */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4">Signature du responsable</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="responsibleName">Nom du responsable</Label>
                    <Input
                      id="responsibleName"
                      name="responsibleName"
                      value={formData.responsibleName}
                      onChange={handleInputChange}
                      placeholder="Nom complet"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Signature</Label>
                    <div className="border-2 border-gray-300 rounded-lg">
                      <SignatureCanvas
                        ref={signatureRef}
                        canvasProps={{
                          className: 'w-full h-40',
                        }}
                      />
                    </div>
                    <Button type="button" onClick={clearSignature} size="sm" variant="outline">
                      Effacer la signature
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>

            <div className="flex justify-end space-x-2 p-6 border-t">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Création...' : 'Créer le rapport'}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Liste des rapports */}
      <div className="grid grid-cols-1 gap-4">
        <h2 className="text-xl font-semibold">Rapports créés</h2>
        
        {reports.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucun rapport créé</p>
            </CardContent>
          </Card>
        ) : (
          reports.map((report) => (
            <Card key={report.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{report.siteName}</h3>
                    <p className="text-sm text-gray-600">
                      Période: {new Date(report.periodStart).toLocaleDateString()} - {new Date(report.periodEnd).toLocaleDateString()}
                    </p>
                    <p className="text-sm text-gray-500">
                      Créé le {new Date(report.createdAt).toLocaleDateString()} par {report.generatedByName}
                    </p>
                    <div className="flex items-center space-x-4 mt-2">
                      <span className="text-sm bg-red-100 text-red-800 px-2 py-1 rounded">
                        {report.totalIncidents} incident(s)
                      </span>
                      <span className={`text-sm px-2 py-1 rounded ${
                        report.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                        report.status === 'finalized' ? 'bg-green-100 text-green-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {report.status === 'draft' ? 'Brouillon' :
                         report.status === 'finalized' ? 'Finalisé' : 'Exporté'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleExport(report.id, 'pdf')}
                    >
                      <FileDown className="w-4 h-4 mr-1" />
                      PDF
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleExport(report.id, 'word')}
                    >
                      <FileDown className="w-4 h-4 mr-1" />
                      Word
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleExport(report.id, 'excel')}
                    >
                      <FileDown className="w-4 h-4 mr-1" />
                      Excel
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default SecurityReports;
